/**
 * CustomizeQrPage Component
 *
 * Main page component for QR code customization.
 * Provides a two-column layout with preview on the left and controls on the right.
 * Uses Topgolf font for the page title per typography guidelines.
 *
 * Requirements:
 * - 1.1: Display up to three QR code previews simultaneously
 * - 1.2: Always display the Site QR preview as the first QR code
 * - 1.3: Display Fixed Bay QR preview when site has fixed bays
 * - 1.4: Display Super Bay QR preview when site has super bays
 * - Advanced Options 1.1: Display Advanced_Section between QR previews and download section
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useSite } from '../../site/context/useSite';
import { useAnalytics } from '../../analytics';
import { useQrTemplate } from '../hooks/useQrTemplate';
import { useAdvancedOptions } from '../hooks/useAdvancedOptions';
import { useAdvancedMode } from '../../hooks/useAdvancedMode';
import { MultiQrPreview } from './MultiQrPreview';
import { CustomizationPanel } from './CustomizationPanel';
import { BatchDownloadButton } from './BatchDownloadButton';
import { AdvancedOptionsSection } from './AdvancedOptionsSection';
import './CustomizeQrPage.css';

/**
 * Generates a tooltip string describing the QR codes to be downloaded.
 * Format: "1 generic site QR, X fixed bays, Y super bays" (skipping X/Y if 0)
 */
function generateDownloadTooltip(fixedBayCount: number, superBayCount: number): string {
  const parts: string[] = ['1 generic site QR'];
  
  if (fixedBayCount > 0) {
    parts.push(`${fixedBayCount} fixed bay${fixedBayCount === 1 ? '' : 's'}`);
  }
  
  if (superBayCount > 0) {
    parts.push(`${superBayCount} super bay${superBayCount === 1 ? '' : 's'}`);
  }
  
  return parts.join(', ');
}

/**
 * Props for the CustomizeQrPage component
 * No props required - uses SiteContext for site data
 */
export interface CustomizeQrPageProps {
  // No props - uses SiteContext for site data
}

/**
 * CustomizeQrPage - Main page component for QR customization
 *
 * Composes MultiQrPreview, CustomizationPanel, and download buttons
 * into a two-column layout.
 *
 * @example
 * ```tsx
 * // Used within a route that has SiteProvider context
 * <CustomizeQrPage />
 * ```
 */
export function CustomizeQrPage(): JSX.Element {
  const { site, ensureKey } = useSite();
  const { track } = useAnalytics();
  const { template, updateTemplate, resetTemplate } = useQrTemplate(site?.siteId);
  const { advancedOptions, updateAdvancedOptions } = useAdvancedOptions();
  const { isAdvancedMode } = useAdvancedMode();

  // Track page view when site is available
  // Requirement 5.5: Track "Pocket Admin Customize QR Viewed" with SiteId
  useEffect(() => {
    if (site) {
      track('Pocket Admin Customize QR Viewed', { SiteId: site.siteId });
    }
  }, [site, track]);

  // Handle logo upload
  const handleLogoUpload = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        updateTemplate({ logoDataUrl: dataUrl });
      };
      reader.readAsDataURL(file);
    },
    [updateTemplate]
  );

  // Handle logo removal
  const handleLogoRemove = useCallback(() => {
    updateTemplate({ logoDataUrl: null });
  }, [updateTemplate]);

  // Generate tooltip for download button
  const downloadTooltip = useMemo(() => {
    if (!site) return '';
    return generateDownloadTooltip(
      site.bays.fixedBays.length,
      site.bays.superBays.length
    );
  }, [site]);

  // Show loading state if site is not loaded
  if (!site) {
    return (
      <div className="customize-qr-page">
        <div className="customize-qr-page__loading">
          <span>Loading site data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="customize-qr-page">
      {/* Page Header */}
      <header className="customize-qr-page__header">
        <div className="customize-qr-page__header-left">
          <h1 className="customize-qr-page__title">QR Codes</h1>
          <span className="customize-qr-page__site-name">{site.name}</span>
        </div>
        
        {/* Download All Button - Top Right */}
        <div className="customize-qr-page__header-actions">
          <BatchDownloadButton
            template={template}
            siteId={site.siteId}
            siteName={site.name}
            fixedBays={site.bays.fixedBays.map((bay) => ({
              bayId: bay.id,
              bayNumber: bay.id,
            }))}
            superBays={site.bays.superBays.map((bay) => ({
              bayId: bay.id,
              bayName: bay.name,
            }))}
            disabled={false}
            advancedOptions={advancedOptions}
            tooltip={downloadTooltip}
          />
        </div>
      </header>

      {/* Main Content - Two Column Layout */}
      <div className="customize-qr-page__content">
        {/* Left Column - Multi-Preview */}
        <div className="customize-qr-page__preview-column">
          <div className="customize-qr-page__preview-section">
            <MultiQrPreview
              template={template}
              siteId={site.siteId}
              siteName={site.name}
              fixedBays={site.bays.fixedBays}
              superBays={site.bays.superBays}
              ensureKey={ensureKey}
              advancedOptions={advancedOptions}
            />
          </div>

          {/* Advanced Options Section - Requirement 1.1 (only visible with advanced=true query param) */}
          {isAdvancedMode && (
            <div className="customize-qr-page__advanced-section">
              <AdvancedOptionsSection
                advancedOptions={advancedOptions}
                onOptionsChange={updateAdvancedOptions}
              />
            </div>
          )}
        </div>

        {/* Right Column - Controls */}
        <div className="customize-qr-page__controls-column">
          <CustomizationPanel
            template={template}
            hasFixedBays={site.bays.fixedBays.length > 0}
            onTemplateChange={updateTemplate}
            onLogoUpload={handleLogoUpload}
            onLogoRemove={handleLogoRemove}
            onReset={resetTemplate}
          />
        </div>
      </div>
    </div>
  );
}
